import { Check, LogOut, Menu, Monitor, Moon, Search, Shield, Sun, User } from 'lucide-react'
import { type ReactNode } from 'react'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
} from '@/components/ui/DropdownMenu'
import { Input } from '@/components/ui/Input'
import { setLocale, useT, getLocale, SUPPORTED_LOCALES } from '@/i18n'
import { useTheme, type ThemeMode } from '@/lib/theme'
import { useAdminSession } from '@/features/auth/session'
import { cn, initialsOf } from '@/lib/utils'

/**
 * The account menu shows the REAL signed-in operator, not a placeholder.
 *
 * `useAdminSession()` gives us the authenticated email and `signOut`. It does
 * not give a display name or a role, because neither lives in `auth.users` —
 * they belong to `admin_users`, which does not exist yet (see CLAUDE.md § 8).
 * So the menu shows the email and nothing else: an invented name beside a real
 * address is worse than no name, because it reads as data.
 */

/**
 * Which deployment this build is. Declared here, where the badge that shows it
 * lives, so `AdminShell` imports the type rather than the two files declaring
 * the same union twice and drifting.
 */
export type AdminEnvironment = 'development' | 'demo' | 'staging' | 'production'

const THEME_ICON: Record<ThemeMode, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
}

export type TopbarProps = {
  onOpenNav: () => void
  onOpenPalette: () => void
  /**
   * Shown as an environment badge; production is deliberately loud.
   *
   * ⚠️ `null` MEANS NOTHING TOLD US, AND IT RENDERS NO BADGE. It is not a
   * synonym for staging or for production — `AdminShell`'s `resolveEnvironment`
   * used to fall back to `'staging'`, which put a "Staging" badge on the
   * client's production panel on every screen because `VITE_ENVIRONMENT` is
   * unset there. An absent badge says nothing; a wrong one says something false.
   */
  environment: AdminEnvironment | null
}

export function Topbar({ onOpenNav, onOpenPalette, environment }: TopbarProps): ReactNode {
  const t = useT()
  const { email, signOut } = useAdminSession()
  const { mode, setMode } = useTheme()
  const ThemeIcon = THEME_ICON[mode]
  const locale = getLocale()

  const shortcut = navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K'

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-card/80 sm:px-4">
      <Button
        variant="ghost"
        size="iconSm"
        className="md:hidden"
        onClick={onOpenNav}
        aria-label={t('nav.openNavigation')}
      >
        <Menu aria-hidden="true" />
      </Button>

      {/* Global search doubles as the palette trigger — one affordance, two jobs. */}
      {/*
        ─── 🔴 13 OCT: THE PLACEHOLDER PROMISED A SEARCH THAT DOES NOT EXIST ──

        It read *"Search users, businesses, requests, payments…"*. A QA pass
        typed `Silje` — a user on page 1 of the Users module — and got **"No
        module matches 'Silje'"**. Typing `elektro` returned nothing; typing
        `pay` returned *Payments & finance* and *Payouts*.

        **This is a module jump-list, and the palette behind it has always said
        so** — its own placeholder is "Jump to a module…" and its empty state
        names modules. Only the box on the page over-promised, and it is the box
        everybody sees first.

        ⚠️ FIXED BY CHANGING THE CLAIM, NOT THE BEHAVIOUR, and that is the
        honest order: a real record search across users, businesses, requests
        and payments is four RLS-bounded queries and a ranked result list, not a
        placeholder edit. **Until somebody builds it, the control should say
        what it does.** A promise is a defect; a narrow feature is not.
      */}
      <div className="min-w-0 flex-1 sm:max-w-lg">
        <Input
          type="search"
          readOnly
          onFocus={(event) => {
            event.currentTarget.blur()
            onOpenPalette()
          }}
          onClick={onOpenPalette}
          placeholder={t('topbar.searchPlaceholder')}
          aria-label={t('topbar.searchLabel')}
          aria-keyshortcuts="Meta+K Control+K"
          className="cursor-pointer"
          startAdornment={<Search aria-hidden="true" />}
          endAdornment={
            <kbd className="hidden rounded-sm border border-border px-1.5 py-0.5 text-overline text-muted-foreground sm:block">
              {shortcut}
            </kbd>
          }
        />
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        {environment === null ? null : (
          <Badge
            variant={environment === 'production' ? 'destructive' : 'warning'}
            className="hidden sm:inline-flex"
            title={t('topbar.environment.label')}
          >
            {t(`topbar.environment.${environment}`)}
          </Badge>
        )}

        <DropdownMenu
          label={t('theme.label')}
          trigger={
            <Button variant="ghost" size="iconSm" aria-label={t('theme.label')}>
              <ThemeIcon aria-hidden="true" />
            </Button>
          }
        >
          <DropdownLabel>{t('theme.label')}</DropdownLabel>
          {(['light', 'dark', 'system'] as const).map((option) => {
            const Icon = THEME_ICON[option]
            return (
              <DropdownItem
                key={option}
                icon={<Icon aria-hidden="true" />}
                selected={mode === option}
                onSelect={() => setMode(option)}
                hint={mode === option ? <Check aria-hidden="true" className="size-3.5" /> : undefined}
              >
                {t(`theme.${option}`)}
              </DropdownItem>
            )
          })}
          <DropdownSeparator />
          {/*
            "Language", not "More" (client feedback, 22 Sep). The group under it
            has only ever held the locales, so the generic `common.more` was
            naming a menu it did not describe. `common.more` is left in the
            catalogue untouched for whatever needs a genuine "More".
          */}
          <DropdownLabel>{t('language.label')}</DropdownLabel>
          {SUPPORTED_LOCALES.map((option) => (
            <DropdownItem
              key={option}
              selected={locale === option}
              onSelect={() => setLocale(option)}
              hint={locale === option ? <Check aria-hidden="true" className="size-3.5" /> : undefined}
            >
              {/*
                ⚠️ A DISPLAY LABEL, NOT THE LOCALE CODE. The client asks for the
                Norwegian option to read "NO"; the code stays `nb` everywhere it
                is stored or compared — `LocaleCode`, the i18n catalogues, every
                `*_translations` table. `option.toUpperCase()` used to render
                "NB", which is why this is a lookup and not a transform: the two
                must be allowed to differ.
              */}
              {t(`language.locales.${option}`)}
            </DropdownItem>
          ))}
        </DropdownMenu>

        {/*
          ═══════════════════════════════════════════════════════════════════════
          🔴 THE NOTIFICATION BELL WAS REMOVED ON 7 OCT 2026. DO NOT PUT IT BACK
          WITHOUT SOMETHING BEHIND IT.

          It was `const UNREAD_NOTIFICATIONS = 3` — a literal in this file. It
          painted a red dot on every screen of the panel, announced
          *"3 unread notifications"* to a screen reader, had no `onClick`, and
          opened nothing. On the client's live URL it was the last fake data in
          the panel and the first thing a client clicks.

          REMOVED RATHER THAN EMPTIED, and that is the choice worth recording.
          An inert bell with no badge still promises a notification centre that
          does not exist; a bell with a real `0` promises one that is merely
          quiet. Neither is true, so the control goes.

          WHAT IT WOULD TAKE TO BRING IT BACK. There is no admin notification
          feed in this schema: `notifications` is per-USER and the admin account
          holds none, so there is nothing to count. A real bell means either that
          table gaining an admin audience, or an aggregate over the queues this
          panel already owns — claims, submissions, identity, reports, disputes,
          support. ⚠️ THE SECOND IS NOT A CLIENT-SIDE COUNT: every one of those
          is RLS-filtered, so `count: 'exact'` from a browser returns the
          readable subset with HTTP 200 and renders as fact (CLAUDE.md § 7). It
          needs one `SECURITY DEFINER` function answering "what needs attention",
          in the shape of `business_covers_point`. Until that exists, the
          dashboard is where those numbers live and it reads them honestly.
          ═══════════════════════════════════════════════════════════════════════
        */}

        <DropdownMenu
          label={t('topbar.account')}
          trigger={
            <Button variant="ghost" size="iconSm" aria-label={t('topbar.account')}>
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-7 items-center justify-center rounded-full',
                  'bg-primary text-overline text-primary-foreground',
                )}
              >
                {initialsOf(email ?? '?')}
              </span>
            </Button>
          }
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-body-sm font-medium text-foreground">{email ?? '—'}</p>
          </div>
          <DropdownSeparator />
          <DropdownItem icon={<User aria-hidden="true" />}>{t('topbar.profile')}</DropdownItem>
          <DropdownItem icon={<Shield aria-hidden="true" />}>{t('topbar.auditTrail')}</DropdownItem>
          <DropdownSeparator />
          {/*
            Sign out was inert until now, which meant an operator on a shared
            machine could not end their session from inside the panel at all.
            `signOut` also clears the query cache, so the next person to sign in
            does not inherit the previous one's cached rows.
          */}
          <DropdownItem
            variant="destructive"
            icon={<LogOut aria-hidden="true" />}
            onSelect={() => void signOut()}
          >
            {t('topbar.signOut')}
          </DropdownItem>
        </DropdownMenu>
      </div>
    </header>
  )
}
